import "./promotionsSlider.scss";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  PROMOTIONS,
  PROMOTIONS_AUTOPLAY_MS,
  type Promotion,
  type PromotionAccent,
} from "../../constants/promotions";

function PromoLightbox({
  promotion,
  accent,
  onClose,
}: {
  promotion: Promotion;
  accent: PromotionAccent;
  onClose: () => void;
}) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <div
      className={`promo-lightbox promo-lightbox--${accent}`}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="promo-lightbox__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <p id={titleId} className="visually-hidden">
          {promotion.alt}
        </p>

        <button
          ref={closeRef}
          type="button"
          className="promo-lightbox__close"
          aria-label="Закрыть"
          onClick={onClose}
        >
          <span aria-hidden="true">×</span>
        </button>

        <div className="promo-lightbox__frame">
          <img
            className="promo-lightbox__image"
            src={promotion.image}
            alt={promotion.alt}
            draggable={false}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}

const PromotionsSlider = () => {
  const slides = PROMOTIONS;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lightbox, setLightbox] = useState<Promotion | null>(null);
  const reduceMotionRef = useRef(false);

  const count = slides.length;
  const lightboxOpen = lightbox !== null;

  const goTo = useCallback(
    (next: number) => {
      if (count === 0) return;
      setIndex(((next % count) + count) % count);
    },
    [count]
  );

  const goPrev = useCallback(() => goTo(index - 1), [goTo, index]);
  const goNext = useCallback(() => goTo(index + 1), [goTo, index]);
  const closeLightbox = useCallback(() => setLightbox(null), []);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reduceMotionRef.current = mq.matches;
    const onChange = () => {
      reduceMotionRef.current = mq.matches;
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (
      count < 2 ||
      paused ||
      lightboxOpen ||
      PROMOTIONS_AUTOPLAY_MS <= 0 ||
      reduceMotionRef.current
    ) {
      return;
    }

    const id = window.setInterval(() => {
      setIndex((current) => (current + 1) % count);
    }, PROMOTIONS_AUTOPLAY_MS);

    return () => window.clearInterval(id);
  }, [count, paused, lightboxOpen]);

  if (count === 0) return null;

  const active = slides[index];
  const accent = active.accent ?? "gold";
  const lightboxAccent = lightbox?.accent ?? accent;

  return (
    <section
      className={`promotions-slider promotions-slider--${accent}`}
      aria-roledescription="carousel"
      aria-label="Акции и предложения"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setPaused(false);
        }
      }}
    >
      <div className="promotions-slider__glow" aria-hidden="true" />

      <div className="promotions-slider__inner container">
        <div className="promotions-slider__header">
          <p className="section-label promotions-slider__eyebrow">
            Акции и предложения
          </p>
        </div>

        <div className="promotions-slider__stage">
          {count > 1 && (
            <button
              type="button"
              className="promotions-slider__nav promotions-slider__nav--prev"
              onClick={goPrev}
              aria-label="Предыдущий слайд"
            >
              <svg
                className="promotions-slider__nav-icon"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M15.5 4.5 8 12l7.5 7.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}

          <div className="promotions-slider__viewport">
            {slides.map((slide, i) => {
              const isActive = i === index;
              return (
                <article
                  key={slide.id}
                  className={`promotions-slider__slide${
                    isActive ? " is-active" : ""
                  }`}
                  aria-hidden={!isActive}
                  aria-roledescription="slide"
                  aria-label={`${i + 1} из ${count}: ${slide.alt}`}
                >
                  <button
                    type="button"
                    className="promotions-slider__frame"
                    onClick={() => setLightbox(slide)}
                    aria-label={`Открыть крупнее: ${slide.alt}`}
                  >
                    <img
                      className="promotions-slider__image"
                      src={slide.image}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                    />
                  </button>
                </article>
              );
            })}
          </div>

          {count > 1 && (
            <button
              type="button"
              className="promotions-slider__nav promotions-slider__nav--next"
              onClick={goNext}
              aria-label="Следующий слайд"
            >
              <svg
                className="promotions-slider__nav-icon"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M8.5 4.5 16 12l-7.5 7.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>

        {count > 1 && (
          <div
            className="promotions-slider__dots"
            role="tablist"
            aria-label="Выбор слайда"
          >
            {slides.map((slide, i) => (
              <button
                key={slide.id}
                type="button"
                role="tab"
                className={`promotions-slider__dot${
                  i === index ? " is-active" : ""
                }`}
                aria-selected={i === index}
                aria-label={`Слайд ${i + 1}: ${slide.alt}`}
                onClick={() => goTo(i)}
              />
            ))}
          </div>
        )}
      </div>

      {lightbox && (
        <PromoLightbox
          promotion={lightbox}
          accent={lightboxAccent}
          onClose={closeLightbox}
        />
      )}
    </section>
  );
};

export default PromotionsSlider;
